import axios from "axios";
import * as Sentry from "@sentry/react";

// Type definitions for the function parameters
interface AttributionData {
  currentUrl: string;
  referrer?: string;
  fbclid?: string | null;
  utm_campaign?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_term?: string | null;
  utm_content?: string | null;
  username?: string | null;
  phone_number?: string | null;
  eventName: string | null;
  additionalData?: {
    success?: boolean;
    sale?: {
      product_type?: string;
      amount_inr?: number;
    };
  } | null;
}

// Helper function to get a cookie by name
export const getCookie = (name: string): string | null => {
  const cookieArr = document.cookie.split(";");
  for (const cookie of cookieArr) {
    const [key, value] = cookie.trim().split("=");
    if (key === name) {
      return value;
    }
  }
  return null; // Return null if the cookie does not exist
};

// Helper function to set a cookie
export const setCookie = (name: string, value: string, days: number) => {
  const expires = new Date();
  expires.setDate(expires.getDate() + days);
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; domain=nationalfinanceolympiad.com; secure; SameSite=None`;
};

// Helper function to conditionally include properties in an object
const conditionalInclude = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const result: Partial<T> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== null && value !== undefined && value !== '') {
      result[key as keyof T] = value;
    }
  }
  return result;
};

export const sendAttributionData = async ({
  currentUrl = "",
  referrer = document.referrer || "",
  fbclid = null,
  utm_campaign = null,
  utm_source = null,
  utm_medium = null,
  utm_term = null,
  utm_content = null,
  username = null,
  phone_number = null,
  eventName = null,
  additionalData = null,
}: AttributionData): Promise<any> => {
  if (!currentUrl) {
    console.error("currentUrl is required");
    return;
  }

  // Retrieve session_id from cookies
  const session_id = getCookie("session_id") || null;
  const fbc = getCookie("_fbc") || null;
  const fbp = getCookie("_fbp") || null;

  const ga = getCookie("_ga") || null;
  const ga_measurement= getCookie("_ga_ZGFSQKYL39") || null;

  const exp_name = sessionStorage.getItem("exp_name") || null;
  const exp_variant = sessionStorage.getItem("exp_variant") || null;
  const exp_id = sessionStorage.getItem("exp_id") || null;

  const filled_username = sessionStorage.getItem("firstName") + " " + sessionStorage.getItem("lastName") || null;
  const filled_phone_number = sessionStorage.getItem("mobileNumber") || null;

  try {
    const url = new URL(currentUrl);

    const data = {
      currentUrl: url.hostname,
      currentPath: url.pathname,
      referrer,
      source: "preorder-checkout",
      event_name: eventName,
      additional_data: {
        event_name: eventName,
        ...additionalData,
      },
      // Conditionally include tracking parameters only if they exist
      ...conditionalInclude({
        fbclid,
        fbc,
        fbp,
        ga,
        ga_measurement,
        exp_name,
        exp_variant,
        exp_id,
        utm_campaign,
        utm_source,
        utm_medium,
        utm_term,
        utm_content,
      }),
    };

    const payload = {
      data,
      // Conditionally include user data only if they exist
      ...conditionalInclude({
        username: filled_username,
        session_id,
        phone_number: filled_phone_number,
      }),
    };

    const response = await axios.post(
      "https://admin.arb.wiki/channelAttribution/store-attribution",
      payload,
      {
        headers: {
          "x-api-key": "lZxTPXggnP4ygq4PKaVjCQWdRVCYFrz4uxwWf3H3",
          "Content-Type": "application/json",
        },
      }
    );

    console.log("API Response:", response.data);

    if (response.data.session_id && !session_id) {
      setCookie("session_id", response.data.session_id, 30);
      console.log("New session_id saved in cookies:", response.data.session_id);
    }

    return response.data;
  } catch (err: any) {
    console.error("Error sending attribution data:", err);
    throw err;
  }
};

export const trackData = async (
  eventName: string | null,
  userName?: string | null,
  phoneNumber?: string | null,
  additionalData?: {
    success?: boolean;
    payment_type?: string;
    sale?: {
      product_type?: string;
      amount_inr?: number;
      order_id?: string;
    };
  }
) => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    
    const response = await sendAttributionData({
      currentUrl: window.location.href,
      referrer: document.referrer,
      eventName: eventName,
      additionalData: additionalData,
      // Conditionally include URL parameters only if they exist
      ...conditionalInclude({
        fbclid: urlParams.get("fbclid"),
        utm_campaign: urlParams.get("utm_campaign"),
        utm_term: urlParams.get("utm_term"),
        utm_content: urlParams.get("utm_content"),
        utm_source: urlParams.get("utm_source"),
        utm_medium: urlParams.get("utm_medium"),
        username: userName,
        phone_number: phoneNumber,
      }),
    });
    console.log("Tracking response:", response);
  } catch (err) {
    console.error("Error tracking data:", err);
    const userName = sessionStorage.getItem("username") || null;
    const phoneNumber = sessionStorage.getItem("phone_number") || null;
    Sentry.captureException(err);
  }
};

export const getEventNameFromItemIds = (itemIds: string): string => {
  console.log("itemids value", itemIds);
  const ids = itemIds.split("-").map(Number);

  // // If NFO Registration (3) is present with other items
  // if (ids.includes(3)) {
  //   if (ids.includes(1) && ids.includes(2)) {
  //     return "nfo_registration_with_personal_finance_handbook_grade_6_8_and_10_12_payment_gateway";
  //   } else if (ids.includes(1)) {
  //     return "nfo_registration_with_personal_finance_handbook_grade_6_8_payment_gateway";
  //   } else if (ids.includes(2)) {
  //     return "nfo_registration_with_personal_finance_handbook_grade_10_12_payment_gateway";
  //   }
  // }

  // If only single item
  if (ids.length === 1) {
    switch (ids[0]) {
      case 1:
        return "personal_finance_handbook_grade_1_and_2_payment_gateway";
      case 2:
        return "personal_finance_handbook_grade_3_and_4_payment_gateway";
      default:
        return "unknown_payment_gateway";
    }
  }


  return "unknown_payment_gateway";
};
